<template>
  <div class="address-form">
    <Input
      placeholder="Address Line 1"
      :inputValue.sync="addLine1Data"
      :isDisabled="viewMode"
      :isRequired="addressRequired.addressLine1"
      :errorMessage.sync="addLine1Error"
      validator="text,40"
      ref="address1"
    />
    <Input
      placeholder="Address Line 2"
      :inputValue.sync="addLine2Data"
      :isDisabled="viewMode"
      :isRequired="addressRequired.addressLine2"
      :errorMessage.sync="addLine2Error"
      validator="text,40"
      ref="address2"
    />
    <Input
      placeholder="Address Line 3"
      :inputValue.sync="addLine3Data"
      :isDisabled="viewMode"
      :isRequired="addressRequired.addressLine3"
      :errorMessage.sync="addLine3Error"
      validator="text,40"
      ref="address3"
    />
    <Input
      placeholder="Address Line 4"
      :inputValue.sync="addLine4Data"
      :isDisabled="viewMode"
      :isRequired="addressRequired.addressLine4"
      :errorMessage.sync="addLine4Error"
      validator="text,40"
      ref="address4"
    />
    <div class="flex-row">
      <div class="item-container">
        <div v-if="postcodeLoading" class="api-loading" name="Post Code"></div>
        <Dropdown
          v-if="nothingOrMalaysiaIsSelected"
          placeholder="Postcode"
          :items="postcodeData"
          :selectedObject.sync="selectedPostcodeData"
          :isDisabled="viewMode"
          itemKey="PostCode"
          :isRequired="addressRequired.postcode"
          :errorMessage.sync="postcodeError"
          ref="postcodeDropdown"
        />
        <Input
          v-else
          placeholder="Postcode"
          :inputValue.sync="postcodeInputValue"
          :isDisabled="viewMode"
          :isRequired="addressRequired.postcode"
          :errorMessage.sync="postcodeError"
          validator="text,10,uppercase,alphanumeric,all"
          ref="postcodeInput"
        />
      </div>
      <Input
        placeholder="City"
        :inputValue.sync="cityData"
        :isDisabled="viewMode"
        :isRequired="addressRequired.city"
        :errorMessage.sync="cityError"
        ref="city"
        validator="text,30,uppercase,alphabetic"
      />
    </div>
    <div class="flex-row">
      <div class="item-container">
        <div v-if="stateLoading" class="api-loading" name="State"></div>
        <Dropdown
          v-if="nothingOrMalaysiaIsSelected"
          placeholder="State"
          :items="stateData"
          :selectedObject.sync="selectedStateData"
          :isDisabled="viewMode"
          :isRequired="addressRequired.state"
          itemKey="State"
          :errorMessage.sync="stateError"
          ref="stateDropdown"
        />
        <Input
          v-else
          placeholder="State"
          :inputValue.sync="stateInputValue"
          :isDisabled="viewMode"
          :isRequired="addressRequired.state"
          :errorMessage.sync="stateError"
          validator="text,30,uppercase,alphabetic,all"
          ref="stateInput"
        />
      </div>
      <div class="item-container">
        <div v-if="countryLoading" class="api-loading" name="Country"></div>
        <Dropdown
          placeholder="Country"
          :items="countryData"
          :selectedObject.sync="selectedCountryData"
          :isDisabled="viewMode"
          :isRequired="addressRequired.country"
          itemKey="Country"
          :errorMessage.sync="countryError"
          ref="country"
        />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},
  computed: {
    nothingOrMalaysiaIsSelected: function () {
      if (!this.selectedCountryData || Object.keys(this.selectedCountryData).length === 0) {
        return true; // nothing is selected
      }

      return this.selectedCountryData?.['Country Code'] === 'MY'; // check if malaysia is selected
    },
  },
  data: function () {
    return {
      addLine1Data: '',
      addLine1Error: '',
      addLine2Data: '',
      addLine2Error: '',
      addLine3Data: '',
      addLine3Error: '',
      addLine4Data: '',
      addLine4Error: '',

      postcodeLoading: false,
      postcodeData: [],
      selectedPostcodeData: null,
      postcodeInputValue: '',
      postcodeError: '',

      cityData: '',
      cityError: '',

      stateLoading: false,
      stateData: [],
      selectedStateData: null,
      stateInputValue: '',
      stateError: '',

      countryLoading: false,
      countryData: [],
      selectedCountryData: null,
      countryError: '',

      flagFirstLoad: true,
    };
  },
  props: {
    apiLoad: { type: Boolean, default: true }, // controls whether this component will get the master lists by itself; set to false to prevent that
    viewMode: { type: Boolean, default: false }, // enables/disables all controls

    postcodes: {
      type: Array,
      default: function () {
        return [];
      },
    }, // populates the postcode dropdown if provided
    states: {
      type: Array,
      default: function () {
        return [];
      },
    }, // populates the states dropdown if provided
    countries: {
      type: Array,
      default: function () {
        return [];
      },
    }, // populates the countries dropdown if provided

    address: {
      type: Object,
      default: function () {
        return {
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          addressLine4: '',
          postcode: '',
          city: '',
          state: '',
          country: '',
        };
      },
    }, // populate each control (use .sync)

    addressRequired: {
      type: Object,
      default: function () {
        return {
          addressLine1: false,
          addressLine2: false,
          addressLine3: false,
          addressLine4: false,
          postcode: false,
          city: false,
          state: false,
          country: false,
        };
      },
    }, // display required indicator for each control

    addressErrors: {
      type: Object,
      default: function () {
        return {
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          addressLine4: '',
          postcode: '',
          city: '',
          state: '',
          country: '',
        };
      },
    }, // display errors for each control (use .sync)

    // Public Methods
    // setFocus(val)
  },
  methods: {
    // Public Methods
    // val can be either 'address1', 'address2', 'address3', 'address4, postcode, city, state, country
    setFocus: function (val) {
      switch (val) {
        case 'address1':
        case 'address2':
        case 'address3':
        case 'address4':
        case 'city':
        case 'country':
          this.$refs[val].setFocus();
          break;

        case 'postcode':
          if (this.nothingOrMalaysiaIsSelected) {
            this.$refs.postcodeDropdown.setFocus();
          } else {
            this.$refs.postcodeInput.setFocus();
          }
          break;

        case 'state':
          if (this.nothingOrMalaysiaIsSelected) {
            this.$refs.stateDropdown.setFocus();
          } else {
            this.$refs.stateInput.setFocus();
          }
          break;
      }
    },

    populateAddressLineAndCityData: function (addressData) {
      this.addLine1Data = addressData.addressLine1 || '';
      this.addLine2Data = addressData.addressLine2 || '';
      this.addLine3Data = addressData.addressLine3 || '';
      this.addLine4Data = addressData.addressLine4 || '';
      this.cityData = addressData.city || '';
    },

    populatePostCodeStateCountrytData: function (addressData) {
      // country
      if (!this.countryData || this.countryData.length === 0 || !addressData.country) {
        // if the country array isn't populated yet or this.address.country is blank
        this.selectedCountryData = null; // clear off state dropdown
      } else {
        const country = this.countryData.find((s) => s['Country'].toUpperCase() == addressData.country.toUpperCase());
        this.selectedCountryData = country || null;
      }

      // postcode
      if (!addressData.postcode) {
        // if nothing was passed for postcode
        this.selectedPostcodeData = null; // clear both the dropdown and the input
        this.postcodeInputValue = '';
      } else if (this.nothingOrMalaysiaIsSelected) {
        if (!this.postcodeData || this.postcodeData.length === 0) {
          // if the postcode array isn't populated yet
          this.selectedPostcodeData = null; // clear the dropdown
        } else {
          const postcode = this.postcodeData.find(
            (s) => s['PostCode'].toUpperCase() == addressData.postcode.toUpperCase(),
          ); // attempt to populate the dropdown
          this.selectedPostcodeData = postcode || null;
        }
      } else {
        // a country other than Malaysia is selected
        this.postcodeInputValue = addressData.postcode; // populate the input
      }

      // state
      if (!addressData.state) {
        // if nothing was passed for state
        this.selectedStateData = null; // clear both the dropdown and the input
        this.stateInputValue = '';
      } else if (this.nothingOrMalaysiaIsSelected) {
        if (!this.stateData || this.stateData.length === 0) {
          // if the state array isn't populated yet
          this.selectedStateData = null; // clear the dropdown
        } else {
          const state = this.stateData.find((s) => s['State'].toUpperCase() == addressData.state.toUpperCase()); // attempt to populate the dropdown
          this.selectedStateData = state || null;
        }
      } else {
        // a country other than Malaysia is selected
        this.stateInputValue = addressData.state.toUpperCase();
      }
    },

    updateAddress: function (addressData) {
      this.populatePostCodeStateCountrytData(addressData);
      this.populateAddressLineAndCityData(addressData);
    },
  },
  async mounted() {
    this.populateAddressLineAndCityData(this.address);
    if (this.addressErrors == null) {
      this.$emit('update:addressErrors', {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        postcode: '',
        city: '',
        state: '',
        country: '',
      });
    }
    this.addLine1Error = this.addressErrors['addressLine1'] || '';
    this.addLine2Error = this.addressErrors['addressLine2'] || '';
    this.addLine3Error = this.addressErrors['addressLine3'] || '';
    this.addLine4Error = this.addressErrors['addressLine4'] || '';
    this.cityError = this.addressErrors['city'] || '';
    this.postcodeError = this.addressErrors['postcode'] || '';
    this.stateError = this.addressErrors['state'] || '';
    this.countryError = this.addressErrors['country'] || '';

    let masterFilePromises = [];

    if (this.postcodes.length == 0 && this.apiLoad) {
      this.postcodeLoading = true; // hide loader upon masterFilePromises all settled
      masterFilePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'postcode' }).then((r) => {
          this.postcodeData = this.$deepTrimJsonV3(r);
          this.$emit('update:postcodes', this.postcodeData);
        }),
      );
    } else if (this.postcodes.length > 0) {
      this.postcodeData = this.postcodes;
    }

    if (this.states.length == 0 && this.apiLoad) {
      this.stateLoading = true; // hide loader upon masterFilePromises all settled
      masterFilePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'state' }).then((r) => {
          this.stateData = this.$deepTrimJsonV3(r);
          this.$emit('update:states', this.stateData);
        }),
      );
    } else if (this.states.length > 0) {
      this.stateData = this.states;
    }

    if (this.countries.length == 0 && this.apiLoad) {
      this.countryLoading = true; // hide loader upon masterFilePromises all settled
      masterFilePromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'country' }).then((r) => {
          this.countryData = this.$deepTrimJsonV3(r);
          this.$emit('update:countries', this.countryData);
        }),
      );
    } else if (this.countries.length > 0) {
      this.countryData = this.countries;
    }

    await Promise.allSettled(masterFilePromises); // wait for all masterfiles to finish loading before populating postcode, state and country
    this.postcodeLoading = false; // keep all 3 loaders visible until all masterfiles finish loading
    this.stateLoading = false;
    this.countryLoading = false;

    this.populatePostCodeStateCountrytData(this.address);
    this.flagFirstLoad = false; //set to false so that it will run the postcode watcher changes
  },
  watch: {
    //#region prop watchers
    postcodes: function (val) {
      if (val) {
        if (val.length > 0) {
          this.postcodeLoading = false;
          this.postcodeData = val;

          if (this.address['postcode']) {
            let postcode = this.postcodeData.find(
              (s) => s['PostCode'].toUpperCase() == this.address['postcode'].toUpperCase(),
            );
            this.selectedPostcodeData = postcode || null;
          }
        }
      }
    },
    states: function (val) {
      if (val) {
        if (val.length > 0) {
          this.stateLoading = false;
          this.stateData = val;

          if (this.address['state']) {
            let state = this.stateData.find((s) => s['State'].toUpperCase() == this.address['state'].toUpperCase());
            this.selectedStateData = state || null;
          }
        }
      }
    },
    countries: function (val) {
      if (val) {
        if (val.length > 0) {
          this.countryLoading = false;
          this.countryData = val;

          if (this.address['country']) {
            let country = this.countryData.find(
              (s) => s['Country'].toUpperCase() == this.address['country'].toUpperCase(),
            );
            this.selectedCountryData = country || null;
          }
        }
      }
    },
    address: {
      handler(newVal) {
        this.updateAddress(newVal);
      },
      deep: true,
    },
    'addressErrors.addressLine1': function (val) {
      this.addLine1Error = val || '';
    },
    'addressErrors.addressLine2': function (val) {
      this.addLine2Error = val || '';
    },
    'addressErrors.addressLine3': function (val) {
      this.addLine3Error = val || '';
    },
    'addressErrors.addressLine4': function (val) {
      this.addLine4Error = val || '';
    },
    'addressErrors.postcode': function (val) {
      this.postcodeError = val || '';
    },
    'addressErrors.city': function (val) {
      this.cityError = val || '';
    },
    'addressErrors.state': function (val) {
      this.stateError = val || '';
    },
    'addressErrors.country': function (val) {
      this.countryError = val || '';
    },
    //#endregion

    //#region data watchers
    addLine1Data: function (val) {
      this.$emit('update:address', {
        ...this.address,
        addressLine1: val,
      });
    },
    addLine1Error: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        addressLine1: val,
      });
    },
    addLine2Data: function (val) {
      this.$emit('update:address', {
        ...this.address,
        addressLine2: val,
      });
    },
    addLine2Error: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        addressLine2: val,
      });
    },
    addLine3Data: function (val) {
      this.$emit('update:address', {
        ...this.address,
        addressLine3: val,
      });
    },
    addLine3Error: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        addressLine3: val,
      });
    },
    addLine4Data: function (val) {
      this.$emit('update:address', {
        ...this.address,
        addressLine4: val,
      });
    },
    addLine4Error: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        addressLine4: val,
      });
    },
    selectedPostcodeData: function (val) {
      if (!this.nothingOrMalaysiaIsSelected) {
        return; // don't do anything if a country other than Malaysia is selected
      }

      if (!this.flagFirstLoad && val) {
        let state = this.stateData.find((s) => s['State'].toUpperCase() == val['State'].toUpperCase());
        let country = this.countryData.find((s) => s['Country'].toUpperCase() == val['Country'].toUpperCase());

        this.cityData = val['City'] ? val['City'].toUpperCase() : '';
        this.selectedStateData = state || null;
        this.selectedCountryData = country || null;

        this.$emit('update:address', {
          ...this.address,
          postcode: val ? val['PostCode'] : '',
          city: this.cityData,
          state: this.selectedStateData?.State ?? '',
          country: this.selectedCountryData?.Country ?? '',
        });
      }
    },
    postcodeInputValue: function (val) {
      if (this.nothingOrMalaysiaIsSelected) {
        return; // don't do anything if country is blank or Malaysia is selected
      }

      this.$emit('update:address', {
        ...this.address,
        postcode: val,
      });
    },
    postcodeError: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        postcode: val,
      });
    },
    cityData: {
      handler: function (val) {
        this.$emit('update:address', {
          ...this.address,
          city: val,
        });
      },
    },
    cityError: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        city: val,
      });
    },
    selectedStateData: function (val) {
      if (!this.nothingOrMalaysiaIsSelected) {
        return; // don't do anything if a country other than Malaysia is selected
      }

      this.$emit('update:address', {
        ...this.address,
        state: val ? val['State'] : '',
      });
    },
    stateInputValue: function (val) {
      if (this.nothingOrMalaysiaIsSelected) {
        return; // don't do anything if country is blank or Malaysia is selected
      }

      this.$emit('update:address', {
        ...this.address,
        state: val,
      });
    },
    stateError: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        state: val,
      });
    },
    selectedCountryData: function (val) {
      this.$emit('update:address', {
        ...this.address,
        country: val ? val['Country'] : '',
      });
    },
    countryError: function (val) {
      this.$emit('update:addressErrors', {
        ...this.addressErrors,
        country: val,
      });
    },
    //#endregion

    //#region computed watchers
    nothingOrMalaysiaIsSelected: function (val) {
      if (val) {
        // country dropdown is blank or Malaysia is selected
        this.$emit('update:address', {
          ...this.address,
          postcode: this.selectedPostcodeData?.PostCode ?? '',
          state: this.selectedStateData?.State ?? '',
        });
      } else {
        this.$emit('update:address', {
          ...this.address,
          postcode: this.postcodeInputValue,
          state: this.stateInputValue,
        });
      }
    },
    //#endregion
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.address-form {
  > .flex-row {
    display: flex;

    > * {
      width: 100%;
    }

    > :not(:first-child) {
      margin-left: 5px;
    }

    > .item-container {
      position: relative;

      > .api-loading {
        position: absolute;
        left: 0;
        top: 0;
        height: 90%;
        width: 100%;
        background-color: rgba(100, 100, 100, 0.5);
        z-index: 10;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;

        &::before {
          content: 'Please wait, loading ' attr(name);
        }
      }
    }
  }
}
</style>
